import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import {
  AreaSeries,
  BarSeries,
  CandlestickSeries,
  ColorType,
  createChart,
  CrosshairMode,
  HistogramSeries,
  LineSeries,
  LineStyle,
  PriceScaleMode,
  TickMarkType,
  type IChartApi,
  type IPriceLine,
  type ISeriesApi,
  type SeriesType,
  type Time,
  type UTCTimestamp,
} from 'lightweight-charts';
import {
  breakEvenPrice,
  closePrice,
  D,
  fmt,
  fmtCompact,
  fmtUsdSigned,
  heikinAshi,
  pnl,
  TF_SECONDS,
  usdPerPoint,
  type Candle,
  type ChartLine,
  type ChartLineStyle,
  type ChartType,
  type Order,
  type Position,
  type Timeframe,
} from '@platform/shared';
import { api, ApiError } from '../lib/api';
import { cssVar, useTheme } from '../lib/theme';
import { useChannel } from '../lib/ws';
import { chartColor, setChartCandle, setChartSettingsOpen, useChartLook, useChartPrefs, useChartSettingsOpen } from '../stores/chart';
import { marketStore, useInstrument, useTicker } from '../stores/market';
import { tradingStore, useOrders, usePositions } from '../stores/trading';
import { useManagedPositions } from '../stores/managed';
import { ChartSettings } from './ChartSettings';
import { cx, Icon, sideLabel, Spinner } from './ui';

const TFS: { id: Timeframe; label: string }[] = [
  { id: '1m', label: '1m' },
  { id: '5m', label: '5m' },
  { id: '15m', label: '15m' },
  { id: '1h', label: '1h' },
  { id: '4h', label: '4h' },
  { id: '1d', label: '1D' },
];

interface Bar {
  t: number;
  o: number;
  h: number;
  l: number;
  c: number;
  v: number;
}
const toBar = (c: Candle): Bar => ({ t: c.t, o: +c.o, h: +c.h, l: +c.l, c: +c.c, v: +c.v });
const ts = (t: number) => (t / 1000) as UTCTimestamp;

function savedTf(): Timeframe {
  try {
    const t = localStorage.getItem('chartTf') as Timeframe;
    if (TFS.some((x) => x.id === t)) return t;
  } catch {
    /* ignore */
  }
  return '15m';
}

type Mode = 'line' | 'candles';
function savedMode(): Mode {
  try {
    return localStorage.getItem('chartMode') === 'candles' ? 'candles' : 'line';
  } catch {
    return 'line';
  }
}
const COMPACT_LABEL: Record<Timeframe, string> = { '1m': '1m', '5m': '5m', '15m': '15m', '1h': '1H', '4h': '4H', '1d': '1D' };

const STYLE: Record<ChartLineStyle, LineStyle> = { solid: LineStyle.Solid, dashed: LineStyle.Dashed, dotted: LineStyle.Dotted };
const SCALE = { normal: PriceScaleMode.Normal, log: PriceScaleMode.Logarithmic, percent: PriceScaleMode.Percentage } as const;
const OHLC_TYPES: ChartType[] = ['candles', 'hollow', 'heikinAshi', 'bars'];

/**
 * Zoom limits. A candle is never narrower than 3px, so zooming out ends with readable candles rather than
 * hairlines; the left edge is pinned so the chart can't be dragged into the space before the first candle.
 * The right edge stays free (pinning it also zeroes the margin that holds the price tag and countdown):
 * the "Back to latest" pill and `settle` handle that side.
 */
const ZOOM = {
  desktop: { barSpacing: 8, minBarSpacing: 3, maxBarSpacing: 40, rightOffset: 8, fixLeftEdge: true },
  compact: { barSpacing: 7, minBarSpacing: 3, maxBarSpacing: 40, rightOffset: 4, fixLeftEdge: true },
} as const;
/** "Back to latest" shows once this share of the canvas, right of the last candle, is empty … */
const LATEST_AT = 0.4;
/** … and a drag that leaves more than this share empty eases back to it. */
const SETTLE_AT = 0.5;
/** The volume pane's height as a share of the price pane's (about 18% of the canvas). */
const VOLUME_PANE = 0.22;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const pad = (n: number) => String(n).padStart(2, '0');
function parts(t: Time, utc: boolean) {
  const d = new Date(Number(t) * 1000);
  return utc
    ? { y: d.getUTCFullYear(), mo: d.getUTCMonth(), d: d.getUTCDate(), wd: d.getUTCDay(), h: d.getUTCHours(), mi: d.getUTCMinutes(), s: d.getUTCSeconds() }
    : { y: d.getFullYear(), mo: d.getMonth(), d: d.getDate(), wd: d.getDay(), h: d.getHours(), mi: d.getMinutes(), s: d.getSeconds() };
}
/** Candles on these timeframes have a time of day worth showing; 1D candles don't. */
const hasTime = (tf: Timeframe) => tf !== '1d';
/** Month labels on these timeframes carry the year, since the axis spans months. */
const spansMonths = (tf: Timeframe) => tf === '4h' || tf === '1d';
/**
 * Time-axis labels: what changed at the tick, in a form nobody has to decode. A day boundary names the day
 * and month ("12 Oct"), a month boundary on an intraday chart the same ("1 Oct"), on a 4-hour or daily
 * chart the month and year ("Oct '25"), a year boundary the year. All within the library's 8-character
 * tick width, so tick spacing is unchanged.
 */
const tickFormatter = (utc: boolean, tf: Timeframe) => (t: Time, type: TickMarkType) => {
  const p = parts(t, utc);
  if (type === TickMarkType.Year) return String(p.y);
  if (type === TickMarkType.Month) return spansMonths(tf) ? `${MONTHS[p.mo]} '${String(p.y).slice(2)}` : `${p.d} ${MONTHS[p.mo]}`;
  if (type === TickMarkType.DayOfMonth) return `${p.d} ${MONTHS[p.mo]}`;
  if (type === TickMarkType.TimeWithSeconds) return `${pad(p.h)}:${pad(p.mi)}:${pad(p.s)}`;
  return `${pad(p.h)}:${pad(p.mi)}`;
};
/** Crosshair label: "Thu 16 Jan 2025", with the time on charts whose candles have one. */
const timeFormatter = (utc: boolean, tf: Timeframe) => (t: Time) => {
  const p = parts(t, utc);
  const date = `${DAYS[p.wd]} ${p.d} ${MONTHS[p.mo]} ${p.y}`;
  return hasTime(tf) ? `${date}, ${pad(p.h)}:${pad(p.mi)}` : date;
};
/** The legend's date for a candle at `t` (ms): "16 Jan 2025", with the time on charts whose candles have one. */
function candleDate(t: number, utc: boolean, tf: Timeframe): string {
  const p = parts(ts(t), utc);
  const date = `${p.d} ${MONTHS[p.mo]} ${p.y}`;
  return hasTime(tf) ? `${date} ${pad(p.h)}:${pad(p.mi)}` : date;
}
/**
 * Prices with thousands grouping and the instrument's decimals, set on the price series rather than the
 * chart's localization: a chart-wide price formatter would format the volume pane's axis like a price too
 * ("10,000,000,000.0" instead of "10B").
 */
const priceFormat = (dp: number) => ({
  type: 'custom' as const,
  minMove: 1 / 10 ** dp,
  formatter: (p: number) => p.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp }),
});
/** The viewer's time zone as the chart labels it: "GMT+1", "EDT" … */
function localZone(): string {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).formatToParts(new Date()).find((x) => x.type === 'timeZoneName')?.value ?? 'Local';
  } catch {
    return 'Local';
  }
}

function countdown(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  return h ? `${h}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}` : `${pad(Math.floor(s / 60))}:${pad(s % 60)}`;
}

/** A line the trader can act on: drag it, or remove it from the chip that shows on hover. */
type TLine =
  | { kind: 'entry'; pos: Position; price: number }
  | { kind: 'tp' | 'sl'; pos: Position; price: number }
  | { kind: 'pending'; order: Order; price: number };

interface Drawn {
  line: IPriceLine;
  t?: TLine;
}

interface DragState {
  t: TLine;
  /** For a dragged position line: the TP or SL it is setting. */
  target: 'tp' | 'sl' | 'pending';
  price: number;
  startY: number;
  moved: boolean;
}

interface Pending {
  t: TLine;
  action: 'tp' | 'sl' | 'pending' | 'remove';
  price: number | null;
  text: string;
}

/**
 * Price chart with live updates and trading lines (entry, TP, SL, pending orders, break-even, bid/ask),
 * styled by the user's chart settings. Desktop: drag TP/SL/pending lines to change them, and a hover chip
 * on each line removes it. `compact` (mobile): no volume or OHLC legend, interval pills below the chart,
 * a line / candles toggle, settings in a bottom sheet, and vertical swipes scroll the page.
 * Every history load starts at the default zoom, scrolled to the latest candle; a "Back to latest" pill
 * appears when the trader has scrolled away from it.
 */
export function ChartPanel({ symbol, compact = false }: { symbol: string; compact?: boolean }) {
  const inst = useInstrument(symbol);
  const ticker = useTicker(symbol);
  const theme = useTheme();
  const look = useChartLook(symbol);
  const prefs = useChartPrefs();
  const allPositions = usePositions();
  const allOrders = useOrders();
  const positions = useMemo(() => allPositions.filter((p) => p.symbol === symbol), [allPositions, symbol]);
  // Trades our dealing team runs on the client's managed account: drawn, but not dragged or closed here.
  const allManaged = useManagedPositions();
  const managed = useMemo(() => allManaged.filter((p) => p.symbol === symbol), [allManaged, symbol]);
  const orders = useMemo(() => allOrders.filter((o) => o.symbol === symbol && o.status === 'pending' && o.limitPrice), [allOrders, symbol]);
  const [tf, setTf] = useState<Timeframe>(savedTf);
  const [legend, setLegend] = useState<Bar | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastC, setLastC] = useState<Bar | null>(null);
  const [mode, setMode] = useState<Mode>(() => (compact ? savedMode() : 'candles'));
  const settings = useChartSettingsOpen();
  const setSettings = setChartSettingsOpen;
  const [gen, setGen] = useState(0); // bumps when the main series is replaced
  const [linesGen, setLinesGen] = useState(0); // bump to redraw the trading lines
  const [overlay, setOverlay] = useState<{
    cd: { y: number; w: number; text: string } | null;
    hover: { y: number; t: TLine } | null;
    latest: { r: number; b: number } | null;
    /** The empty square where the price scale meets the time axis: the time-zone tag sits there. */
    corner: { w: number; h: number } | null;
  }>({ cd: null, hover: null, latest: null, corner: null });
  const [drag, setDrag] = useState<{ y: number; text: string } | null>(null);
  const [confirm, setConfirm] = useState<Pending | null>(null);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const chart = useRef<IChartApi | null>(null);
  const main = useRef<ISeriesApi<SeriesType> | null>(null);
  const volume = useRef<ISeriesApi<'Histogram'> | null>(null);
  const bars = useRef<Bar[]>([]);
  const byTime = useRef(new Map<number, Bar>());
  const loaded = useRef<string>('');
  const drawn = useRef<Drawn[]>([]);
  const bidAsk = useRef<IPriceLine[]>([]);
  const ghost = useRef<IPriceLine | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const dp = inst?.priceDecimals ?? 2;
  const zoom = ZOOM[compact ? 'compact' : 'desktop'];
  const kind: ChartType = compact ? (mode === 'line' ? 'line' : OHLC_TYPES.includes(look.type) ? look.type : 'candles') : look.type;
  const canTrade = !compact;

  const lookRef = useRef(look);
  lookRef.current = look;
  const kindRef = useRef(kind);
  kindRef.current = kind;

  // Create once
  useEffect(() => {
    if (!box.current) return;
    const c = createChart(box.current, {
      autoSize: true,
      crosshair: { mode: CrosshairMode.Normal },
      timeScale: { timeVisible: true, secondsVisible: false, ...zoom },
      ...(compact && {
        handleScroll: { mouseWheel: false, pressedMouseMove: true, horzTouchDrag: true, vertTouchDrag: false },
        handleScale: { mouseWheel: false, pinch: true, axisPressedMouseMove: false, axisDoubleClickReset: true },
      }),
    });
    // Volume gets its own pane (see the look effect), so the price pane keeps only a small margin.
    c.priceScale('right').applyOptions({ scaleMargins: compact ? { top: 0.1, bottom: 0.08 } : { top: 0.06, bottom: 0.06 } });
    c.subscribeCrosshairMove((p) => setLegend(p.time ? (byTime.current.get(Number(p.time) * 1000) ?? null) : null));
    chart.current = c;
    return () => {
      c.remove();
      chart.current = null;
      main.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** Bars in the shape the current series takes. */
  const seriesData = useCallback((rows: Bar[], k: ChartType) => {
    if (k === 'line' || k === 'area') return rows.map((b) => ({ time: ts(b.t), value: b.c }));
    const src = k === 'heikinAshi' ? heikinAshi(rows) : rows;
    return src.map((b) => ({ time: ts(b.t), open: b.o, high: b.h, low: b.l, close: b.c }));
  }, []);

  // Main series: replaced when the chart type changes.
  useEffect(() => {
    const c = chart.current;
    if (!c) return;
    if (main.current) c.removeSeries(main.current);
    const fmtOpt = { priceFormat: priceFormat(dp) };
    main.current =
      kind === 'line'
        ? c.addSeries(LineSeries, { lineWidth: 2, ...fmtOpt })
        : kind === 'area'
          ? c.addSeries(AreaSeries, { lineWidth: 2, ...fmtOpt })
          : kind === 'bars'
            ? c.addSeries(BarSeries, { thinBars: false, ...fmtOpt })
            : c.addSeries(CandlestickSeries, { borderVisible: kind === 'hollow', ...fmtOpt });
    // The price pane outlives the next swap: an empty pane is dropped when the volume pane is under it,
    // and the candles would land in the volume pane.
    c.panes()[0]?.setPreserveEmptyPane(true);
    if (bars.current.length) main.current.setData(seriesData(bars.current, kind) as any);
    setGen((g) => g + 1);
  }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps

  // Look: colours, grid, volume, scale, time zone, last-price line
  useEffect(() => {
    const c = chart.current;
    const s = main.current;
    if (!c || !s) return;
    const up = chartColor(look.upColor);
    const down = chartColor(look.downColor);
    const utc = look.timezone === 'utc';
    c.applyOptions({
      layout: {
        background: { type: ColorType.Solid, color: cssVar('--bg') },
        textColor: cssVar('--muted'),
        fontFamily: 'Geist Mono, monospace',
        fontSize: 11,
        panes: { enableResize: false, separatorColor: cssVar('--border'), separatorHoverColor: 'transparent' },
      },
      grid: { vertLines: { color: cssVar('--grid'), visible: look.grid && !compact }, horzLines: { color: cssVar('--grid'), visible: look.grid } },
      rightPriceScale: { borderColor: cssVar('--border'), mode: SCALE[look.scale] },
      timeScale: { borderColor: cssVar('--border'), tickMarkFormatter: tickFormatter(utc, tf) },
      localization: { timeFormatter: timeFormatter(utc, tf) },
    });
    const last = look.lines.last;
    s.applyOptions({
      priceFormat: priceFormat(dp),
      priceLineVisible: last.show,
      priceLineColor: chartColor(last.color, ''),
      priceLineStyle: STYLE[last.style],
      priceLineWidth: last.width,
      lastValueVisible: look.axisLabels,
    });
    if (kind === 'hollow') s.applyOptions({ upColor: 'rgba(0,0,0,0)', downColor: down, borderUpColor: up, borderDownColor: down, wickUpColor: up, wickDownColor: down } as any);
    else if (kind === 'candles' || kind === 'heikinAshi') s.applyOptions({ upColor: up, downColor: down, wickUpColor: up, wickDownColor: down, borderUpColor: up, borderDownColor: down } as any);
    else if (kind === 'bars') s.applyOptions({ upColor: up, downColor: down } as any);
    else paintLine();
    // Volume: its own pane under the price pane (desktop only), added and removed with the setting.
    if (look.volume && !compact) {
      if (!volume.current) {
        volume.current = c.addSeries(HistogramSeries, { priceFormat: { type: 'volume' }, lastValueVisible: false, priceLineVisible: false }, 1);
        const [price, vol] = c.panes();
        if (price && vol) {
          vol.setStretchFactor(price.getStretchFactor() * VOLUME_PANE);
          vol.priceScale('right').applyOptions({ scaleMargins: { top: 0.2, bottom: 0 } });
        }
      }
      paintVolume();
    } else if (volume.current) {
      c.removeSeries(volume.current); // its pane, now empty, goes with it
      volume.current = null;
    }
  }, [theme, look, gen, dp, tf]); // eslint-disable-line react-hooks/exhaustive-deps

  // History
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loaded.current = '';
    api
      .get<Candle[]>(`/api/candles?symbol=${symbol}&tf=${tf}&limit=1000`)
      .then((rows) => {
        if (cancelled || !main.current) return;
        bars.current = rows.map(toBar);
        byTime.current = new Map(bars.current.map((b) => [b.t, b]));
        main.current.setData(seriesData(bars.current, kindRef.current) as any);
        paintVolume();
        setLastC(bars.current[bars.current.length - 1] ?? null);
        paintLine();
        loaded.current = `${symbol}:${tf}`;
        // Default zoom on every load, so a zoom made on another timeframe or instrument doesn't carry over.
        const ts = chart.current?.timeScale();
        ts?.resetTimeScale();
        ts?.scrollToRealTime();
        requestAnimationFrame(layout);
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [symbol, tf]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live candle
  useChannel<Candle>(`candles:${symbol}:${tf}`, (raw) => {
    if (loaded.current !== `${symbol}:${tf}` || !main.current) return;
    const c = toBar(raw);
    const rows = bars.current;
    const lastBar = rows[rows.length - 1];
    if (lastBar && c.t < lastBar.t) return;
    if (lastBar && lastBar.t === c.t) rows[rows.length - 1] = c;
    else rows.push(c);
    byTime.current.set(c.t, c);
    const k = kindRef.current;
    // Heikin-Ashi bars depend on the whole chain; recomputing it is cheap at 1000 bars.
    main.current.update(seriesData(k === 'heikinAshi' ? rows : [c], k).at(-1) as any);
    volume.current?.update({ time: ts(c.t), value: c.v, color: volColor(c.c >= c.o) });
    setLastC(c);
    paintLine();
    layout();
  });

  /** Line / area colour follows the move over the loaded window. */
  function paintLine() {
    const k = kindRef.current;
    if (k !== 'line' && k !== 'area') return;
    const rows = bars.current;
    const down = rows.length > 1 && rows[rows.length - 1]!.c < rows[0]!.c;
    const col = chartColor(down ? lookRef.current.downColor : lookRef.current.upColor);
    if (k === 'line') main.current?.applyOptions({ color: col } as any);
    else main.current?.applyOptions({ lineColor: col, topColor: withAlpha(col, 0.28), bottomColor: withAlpha(col, 0) } as any);
  }

  /** Volume bars in the candle colours, at half strength. */
  function volColor(up: boolean) {
    return withAlpha(chartColor(up ? lookRef.current.upColor : lookRef.current.downColor), 0.5);
  }

  function paintVolume() {
    if (!volume.current) return;
    const up = volColor(true);
    const down = volColor(false);
    volume.current.setData(bars.current.map((b) => ({ time: ts(b.t), value: b.v, color: b.c >= b.o ? up : down })));
  }

  // ---------- trading lines ----------

  const pnlAt = useCallback(
    (p: Position, price: number | null) => {
      const t = marketStore.get().tickers[symbol];
      if (!inst || !t) return null;
      const exit = price === null ? closePrice(p.side, { bid: D.from(t.bid), ask: D.from(t.ask) }) : D.from(String(price));
      return pnl(p.side, p.entryPrice, exit, p.qty, usdPerPoint(inst.contractSize, t.fx)).add(p.swap);
    },
    [inst, symbol],
  );

  const entryTitle = useCallback(
    (p: Position) => {
      const base = `${sideLabel(p.side)} ${p.leverage}x`;
      const l = lookRef.current;
      if (l.pnl === 'off') return base;
      const u = pnlAt(p, null);
      if (!u) return base;
      if (l.pnl === 'usd') return `${base}  ${fmtUsdSigned(u)}`;
      const m = D.from(p.margin);
      if (!m.isPos()) return base;
      const pct = u.div(m).mul(100);
      return `${base}  ${pct.isNeg() ? '' : '+'}${pct.toFixed(2)}%`;
    },
    [pnlAt],
  );

  useEffect(() => {
    const s = main.current;
    if (!s) return;
    for (const d of drawn.current) s.removePriceLine(d.line);
    for (const l of bidAsk.current) s.removePriceLine(l);
    drawn.current = [];
    bidAsk.current = [];
    const L = look.lines;
    const add = (cfg: ChartLine, price: number, title: string, t?: TLine) => {
      if (!cfg.show) return;
      const line = s.createPriceLine({
        price,
        color: chartColor(cfg.color),
        lineStyle: STYLE[cfg.style],
        lineWidth: cfg.width,
        title: cfg.label ? title : '',
        axisLabelVisible: look.axisLabels,
      });
      drawn.current.push({ line, t });
    };
    for (const p of positions) {
      add(p.side === 'long' ? L.long : L.short, +p.entryPrice, entryTitle(p), { kind: 'entry', pos: p, price: +p.entryPrice });
      if (p.tpPrice) add(L.tp, +p.tpPrice, 'TP', { kind: 'tp', pos: p, price: +p.tpPrice });
      if (p.slPrice) add(L.sl, +p.slPrice, 'SL', { kind: 'sl', pos: p, price: +p.slPrice });
    }
    for (const p of managed) {
      add(p.side === 'long' ? L.long : L.short, +p.entryPrice, `Managed · ${sideLabel(p.side)} ${p.leverage}x`);
      if (p.tpPrice) add(L.tp, +p.tpPrice, 'Managed TP');
      if (p.slPrice) add(L.sl, +p.slPrice, 'Managed SL');
    }
    for (const o of orders) add(L.pending, +o.limitPrice!, `${sideLabel(o.side)} ${o.type}`, { kind: 'pending', order: o, price: +o.limitPrice! });
    const t = marketStore.get().tickers[symbol];
    if (positions.length && inst && t) {
      const upp = usdPerPoint(inst.contractSize, t.fx).toNumber();
      const be = breakEvenPrice(
        positions.map((p) => ({ dir: p.side === 'long' ? 1 : -1, qty: +p.qty, entry: +p.entryPrice, swap: +p.swap, fee: +p.openFee })),
        upp,
      );
      if (be !== null) add(L.breakEven, +be.toFixed(dp), 'Break-even');
    }
    if (L.bidAsk.show && t) {
      for (const [price, title] of [
        [+t.bid, 'Bid'],
        [+t.ask, 'Ask'],
      ] as const) {
        bidAsk.current.push(
          s.createPriceLine({ price, color: chartColor(L.bidAsk.color), lineStyle: STYLE[L.bidAsk.style], lineWidth: L.bidAsk.width, title: L.bidAsk.label ? title : '', axisLabelVisible: look.axisLabels }),
        );
      }
    }
    requestAnimationFrame(layout);
  }, [positions, managed, orders, look.lines, look.axisLabels, look.pnl, theme, gen, linesGen, dp, inst]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live P&L on entry lines, and the bid / ask lines.
  useEffect(() => {
    if (!ticker) return;
    for (const d of drawn.current) if (d.t?.kind === 'entry' && (d.t.pos.side === 'long' ? look.lines.long : look.lines.short).label) d.line.applyOptions({ title: entryTitle(d.t.pos) });
    bidAsk.current[0]?.applyOptions({ price: +ticker.bid });
    bidAsk.current[1]?.applyOptions({ price: +ticker.ask });
  }, [ticker]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- overlay: countdown and hover chips ----------

  const lastRef = useRef<Bar | null>(null);
  lastRef.current = lastC;

  function layout() {
    const c = chart.current;
    const s = main.current;
    const l = lastRef.current;
    if (!c || !s) return;
    let cd: { y: number; w: number; text: string } | null = null;
    if (lookRef.current.countdown && l) {
      const y = s.priceToCoordinate(l.c);
      if (y !== null) cd = { y, w: c.priceScale('right').width(), text: countdown(l.t + TF_SECONDS[tfRef.current] * 1000 - Date.now()) };
    }
    const corner = { w: c.priceScale('right').width(), h: c.timeScale().height() };
    const latest = awayFromLatest() ? { r: corner.w, b: corner.h } : null;
    setOverlay((o) => {
      const hover = o.hover ? relocate(o.hover.t) : null;
      return { cd, hover: hover === null || !o.hover ? null : { y: hover, t: o.hover.t }, latest, corner };
    });
  }
  const tfRef = useRef(tf);
  tfRef.current = tf;

  // ---------- scroll position: "Back to latest", ease back, reset ----------

  /** Visible candles, how many are loaded and the scroll past the last one; null without data. */
  function scroll() {
    const ts = chart.current?.timeScale();
    const r = ts?.getVisibleLogicalRange();
    const n = bars.current.length;
    if (!ts || !r || !n) return null;
    return { ts, n, to: r.to, visible: r.to - r.from, past: ts.scrollPosition() - zoom.rightOffset };
  }

  /** The last candle is scrolled out of view, or more than `LATEST_AT` of the canvas right of it is empty. */
  function awayFromLatest(): boolean {
    const s = scroll();
    if (!s) return false;
    // Fewer candles than fit: the left edge is pinned, so the empty right side can't be scrolled away.
    return s.to < s.n - 1 || (s.n > s.visible && s.past > LATEST_AT * s.visible);
  }

  /** A drag or wheel that left more than half the canvas empty on the right eases back to half. */
  function settle() {
    const s = scroll();
    if (!s || s.n <= s.visible) return;
    const max = SETTLE_AT * s.visible;
    if (s.past > max) s.ts.scrollToPosition(zoom.rightOffset + max, true);
  }
  const settleTimer = useRef(0);
  /** After the gesture, and after the kinetic scroll a touch drag leaves behind. */
  function scheduleSettle() {
    window.clearTimeout(settleTimer.current);
    settleTimer.current = window.setTimeout(settle, 600);
  }
  useEffect(() => () => window.clearTimeout(settleTimer.current), []);

  const toLatest = () => chart.current?.timeScale().scrollToRealTime();
  /** Default zoom, scrolled to the latest candle (what a double-click on the time axis does). */
  const resetView = () => {
    const ts = chart.current?.timeScale();
    ts?.resetTimeScale();
    ts?.scrollToRealTime();
  };

  function relocate(t: TLine): number | null {
    return main.current?.priceToCoordinate(t.price) ?? null;
  }

  useEffect(() => {
    const c = chart.current;
    if (!c) return;
    const on = () => layout();
    c.timeScale().subscribeVisibleLogicalRangeChange(on);
    const ro = new ResizeObserver(on);
    if (box.current) ro.observe(box.current);
    const id = window.setInterval(on, 1000);
    return () => {
      c.timeScale().unsubscribeVisibleLogicalRangeChange(on);
      ro.disconnect();
      window.clearInterval(id);
    };
  }, [gen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => layout(), [look.countdown, tf]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!notice) return;
    const id = window.setTimeout(() => setNotice(null), 4000);
    return () => window.clearTimeout(id);
  }, [notice]);

  // ---------- trading on the chart (desktop) ----------

  const fmtP = (x: number) => fmt(x.toFixed(dp), dp);
  const posName = (p: Position) => `${sideLabel(p.side)} ${p.leverage}x`;

  function lineAt(y: number): TLine | null {
    let best: { t: TLine; d: number } | null = null;
    for (const d of drawn.current) {
      if (!d.t) continue;
      const ly = main.current?.priceToCoordinate(d.t.price);
      if (ly === null || ly === undefined) continue;
      const dist = Math.abs(ly - y);
      if (dist <= 5 && (!best || dist < best.d)) best = { t: d.t, d: dist };
    }
    return best?.t ?? null;
  }

  const localY = (e: { clientY: number }) => e.clientY - (box.current?.getBoundingClientRect().top ?? 0);

  function onMove(e: ReactPointerEvent) {
    if (!canTrade || dragRef.current || confirm) return;
    if ((e.target as HTMLElement).closest('[data-chip]')) return;
    const t = lineAt(localY(e));
    setOverlay((o) => (o.hover?.t === t ? o : { ...o, hover: t ? { y: relocate(t) ?? localY(e), t } : null }));
    if (wrap.current) wrap.current.style.cursor = t && prefs.drag ? 'ns-resize' : '';
  }

  function onDown(e: ReactPointerEvent) {
    if (!canTrade || !prefs.drag || confirm || busy || e.button !== 0) return;
    if ((e.target as HTMLElement).closest('[data-chip],[data-overlay]')) return;
    const t = lineAt(localY(e));
    if (!t) return;
    e.preventDefault();
    e.stopPropagation();
    chart.current?.applyOptions({ handleScroll: false, handleScale: false });
    wrap.current?.setPointerCapture(e.pointerId);
    dragRef.current = { t, target: t.kind === 'entry' ? 'tp' : t.kind, price: t.price, startY: localY(e), moved: false };
    setOverlay((o) => ({ ...o, hover: null }));
  }

  function dragMove(e: ReactPointerEvent) {
    const d = dragRef.current;
    const s = main.current;
    if (!d || !s) return;
    const y = localY(e);
    if (!d.moved && Math.abs(y - d.startY) < 3) return;
    d.moved = true;
    const raw = s.coordinateToPrice(y);
    if (raw === null || raw <= 0) return;
    const price = +raw.toFixed(dp);
    d.price = price;
    if (d.t.kind === 'entry') {
      const above = price > d.t.price;
      d.target = (d.t.pos.side === 'long') === above ? 'tp' : 'sl';
      const cfg = d.target === 'tp' ? lookRef.current.lines.tp : lookRef.current.lines.sl;
      const opts = { price, color: chartColor(cfg.color), lineStyle: STYLE[cfg.style], lineWidth: cfg.width, title: d.target.toUpperCase(), axisLabelVisible: true };
      if (ghost.current) ghost.current.applyOptions(opts);
      else ghost.current = s.createPriceLine(opts);
    } else {
      drawnFor(d.t)?.line.applyOptions({ price });
    }
    setDrag({ y, text: describe(d.t, d.target, price) });
  }

  function dragEnd() {
    const d = dragRef.current;
    if (!d) return;
    dragRef.current = null;
    chart.current?.applyOptions({ handleScroll: true, handleScale: true });
    setDrag(null);
    if (!d.moved || d.price === d.t.price) return revert();
    const p: Pending = { t: d.t, action: d.target, price: d.price, text: describe(d.t, d.target, d.price) };
    if (prefs.confirmDrag) setConfirm(p);
    else void send(p);
  }

  function drawnFor(t: TLine) {
    return drawn.current.find((x) => x.t === t);
  }

  function revert() {
    if (ghost.current) {
      main.current?.removePriceLine(ghost.current);
      ghost.current = null;
    }
    setLinesGen((g) => g + 1);
  }

  function describe(t: TLine, target: 'tp' | 'sl' | 'pending', price: number): string {
    if (t.kind === 'pending') return `Move ${sideLabel(t.order.side).toLowerCase()} ${t.order.type} to ${fmtP(price)}`;
    const u = pnlAt(t.pos, price);
    const verb = (target === 'tp' ? t.pos.tpPrice : t.pos.slPrice) ? 'Move' : 'Set';
    return `${verb} ${target.toUpperCase()} to ${fmtP(price)}${u ? ` · P&L there ${fmtUsdSigned(u)}` : ''}`;
  }

  function askRemove(t: TLine) {
    const text =
      t.kind === 'entry'
        ? `Close ${posName(t.pos)} at market${pnlAt(t.pos, null) ? ` · P&L ${fmtUsdSigned(pnlAt(t.pos, null))}` : ''}`
        : t.kind === 'pending'
          ? `Cancel ${sideLabel(t.order.side).toLowerCase()} ${t.order.type} at ${fmtP(t.price)}`
          : `Remove ${t.kind.toUpperCase()} at ${fmtP(t.price)}`;
    const p: Pending = { t, action: 'remove', price: null, text };
    setOverlay((o) => ({ ...o, hover: null }));
    if (prefs.confirmDrag || t.kind === 'entry') setConfirm(p);
    else void send(p);
  }

  async function send(p: Pending) {
    setBusy(true);
    try {
      const { t } = p;
      if (t.kind === 'pending') {
        if (p.action === 'remove') await api.del(`/api/orders/${t.order.id}`);
        else {
          const o = await api.patch<Order>(`/api/orders/${t.order.id}`, { limitPrice: p.price!.toFixed(dp) });
          tradingStore.set((s) => ({ orders: s.orders.map((x) => (x.id === o.id ? o : x)) }));
        }
        setNotice({ ok: true, text: p.action === 'remove' ? 'Order cancelled' : 'Order moved' });
      } else if (t.kind === 'entry' && p.action === 'remove') {
        await api.post(`/api/positions/${t.pos.id}/close`);
        setNotice({ ok: true, text: 'Position closed' });
      } else {
        const which = p.action === 'remove' ? t.kind : p.action;
        const v = p.price === null ? null : p.price.toFixed(dp);
        const body = { tpPrice: which === 'tp' ? v : t.pos.tpPrice, slPrice: which === 'sl' ? v : t.pos.slPrice };
        const pos = await api.patch<Position>(`/api/positions/${t.pos.id}`, body);
        tradingStore.set((s) => ({ positions: s.positions.map((x) => (x.id === pos.id ? pos : x)) }));
        setNotice({ ok: true, text: p.action === 'remove' ? `${String(which).toUpperCase()} removed` : `${String(which).toUpperCase()} set to ${fmtP(p.price!)}` });
      }
    } catch (e) {
      setNotice({ ok: false, text: e instanceof ApiError ? e.message : 'Could not send the change' });
    } finally {
      setBusy(false);
      setConfirm(null);
      revert();
    }
  }

  // Esc cancels a drag or a pending confirmation.
  useEffect(() => {
    if (!confirm) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setConfirm(null);
      revert();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirm]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close the settings popover on an outside click.
  useEffect(() => {
    if (!settings || compact) return;
    const onDown = (e: MouseEvent) => {
      const el = e.target as HTMLElement;
      if (el.closest('#chart-settings, [aria-controls="chart-settings"]')) return;
      setSettings(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [settings, compact]);
  // The open flag lives in a store (the gear is in the account bar), so close it when leaving the page.
  useEffect(() => () => setChartSettingsOpen(false), []);
  // The price headers show the move since this candle's open, so their change follows the time frame.
  useEffect(() => {
    setChartCandle(lastC && loaded.current === `${symbol}:${tf}` ? { symbol, tf, t: lastC.t, o: lastC.o } : null);
  }, [symbol, tf, lastC?.t, lastC?.o]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => setChartCandle(null), []);

  const pickTf = (id: Timeframe) => {
    setTf(id);
    try {
      localStorage.setItem('chartTf', id);
    } catch {
      /* ignore */
    }
  };

  const shown = legend ?? lastC;
  const up = shown ? shown.c >= shown.o : true;
  const utc = look.timezone === 'utc';
  const zone = useMemo(() => (utc ? 'UTC' : localZone()), [utc]);
  const closeSettings = useCallback(() => setSettings(false), []);

  const overlays = (
    <>
      {overlay.corner && (
        <span
          data-overlay
          title={`Times on the chart are in ${zone}. Change the time zone in chart settings.`}
          className="absolute right-0 bottom-0 z-10 grid place-items-center"
          style={{ width: overlay.corner.w, height: overlay.corner.h }}
        >
          <span className="num rounded-[3px] bg-raised px-1.5 text-[10px] leading-4 whitespace-nowrap text-muted">{zone}</span>
        </span>
      )}
      {overlay.cd && look.countdown && (
        <span data-overlay className="num pointer-events-none absolute right-0 z-10 rounded-sm bg-raised px-1 text-center text-[10px] leading-[15px] text-muted" style={{ top: overlay.cd.y + 10, width: overlay.cd.w }}>
          {overlay.cd.text}
        </span>
      )}
      {overlay.latest && !drag && !confirm && (
        <button
          type="button"
          data-overlay
          onClick={toLatest}
          aria-label="Back to the latest candle"
          className={cx(
            'absolute z-10 flex items-center gap-1.5 rounded-full border border-line-strong bg-panel font-semibold text-fg shadow-[var(--float-shadow)] hover:border-accent',
            compact ? 'h-9 pr-2.5 pl-3.5 text-[13px]' : 'h-7 pr-2 pl-3 text-[11.5px]',
          )}
          style={{ right: overlay.latest.r + 10, bottom: overlay.latest.b + 10 }}
        >
          Back to latest
          <Icon name="toLatest" size={compact ? 14 : 12} />
        </button>
      )}
      {canTrade && overlay.hover && !drag && !confirm && (
        <span data-chip className="absolute left-2 z-20 flex items-center gap-1 rounded-md border border-line-strong bg-panel py-0.5 pr-0.5 pl-2 text-[11px] shadow-sm" style={{ top: overlay.hover.y - 11 }}>
          <span className="text-muted">
            {overlay.hover.t.kind === 'entry' ? posName(overlay.hover.t.pos) : overlay.hover.t.kind === 'pending' ? `${sideLabel(overlay.hover.t.order.side)} ${overlay.hover.t.order.type}` : overlay.hover.t.kind.toUpperCase()}
            {prefs.drag && <span className="ml-1.5 opacity-70">{overlay.hover.t.kind === 'entry' ? 'drag for TP / SL' : 'drag to move'}</span>}
          </span>
          <button
            type="button"
            onClick={() => askRemove(overlay.hover!.t)}
            aria-label={overlay.hover.t.kind === 'entry' ? 'Close position' : overlay.hover.t.kind === 'pending' ? 'Cancel order' : `Remove ${overlay.hover.t.kind.toUpperCase()}`}
            title={overlay.hover.t.kind === 'entry' ? 'Close position' : overlay.hover.t.kind === 'pending' ? 'Cancel order' : `Remove ${overlay.hover.t.kind.toUpperCase()}`}
            className="grid size-5 place-items-center rounded text-muted hover:bg-down-bg hover:text-down"
          >
            <Icon name="close" size={11} />
          </button>
        </span>
      )}
      {drag && (
        <span data-overlay className="num pointer-events-none absolute left-2 z-20 rounded-md border border-line-strong bg-panel px-2 py-0.5 text-[11px] shadow-sm" style={{ top: drag.y - 26 }}>
          {drag.text}
        </span>
      )}
      {confirm && (
        <div data-overlay role="alertdialog" aria-label="Confirm change" className="absolute top-3 left-1/2 z-30 flex w-max max-w-[calc(100%-24px)] -translate-x-1/2 items-center gap-3 rounded-lg border border-line-strong bg-panel px-3 py-2 text-[12px] shadow-[var(--float-shadow)]">
          <span className="num">{confirm.text}</span>
          <button
            type="button"
            onClick={() => {
              setConfirm(null);
              revert();
            }}
            className="h-7 rounded-md border border-line-strong px-2.5 font-semibold"
          >
            Cancel
          </button>
          <button type="button" autoFocus disabled={busy} onClick={() => void send(confirm)} className={cx('h-7 min-w-[64px] rounded-md px-2.5 font-semibold text-white', confirm.action === 'remove' ? 'bg-btn-short' : 'bg-accent')}>
            {busy ? <Spinner /> : 'Confirm'}
          </button>
        </div>
      )}
      {notice && (
        <p role="status" className={cx('absolute bottom-3 left-1/2 z-30 m-0 -translate-x-1/2 rounded-md border border-line-strong bg-panel px-3 py-1.5 text-[12px] shadow-sm', notice.ok ? 'text-up' : 'text-down')}>
          {notice.text}
        </p>
      )}
    </>
  );

  if (compact) {
    return (
      <div>
        <div className="relative h-[clamp(280px,40dvh,380px)]" onPointerUp={scheduleSettle}>
          <div ref={box} className="absolute inset-0" />
          {loading && <span className="absolute top-2 left-4 z-10 text-[13px] text-muted">Loading…</span>}
          {overlays}
        </div>
        <div role="radiogroup" aria-label="Chart interval" className="mt-2 flex items-center justify-between px-3 min-[360px]:px-4">
          {TFS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="radio"
              aria-checked={tf === t.id}
              onClick={() => pickTf(t.id)}
              className={cx(
                'tnum h-10 min-w-10 rounded-[10px] border-[1.5px] px-1.5 text-[15px] font-semibold min-[360px]:min-w-11 min-[360px]:px-2 min-[360px]:text-[16px]',
                tf === t.id ? 'border-up/55 bg-up-bg text-up' : 'border-transparent text-muted',
              )}
            >
              {COMPACT_LABEL[t.id]}
            </button>
          ))}
          <button
            type="button"
            onClick={() => {
              const next = mode === 'line' ? 'candles' : 'line';
              setMode(next);
              try {
                localStorage.setItem('chartMode', next);
              } catch {
                /* ignore */
              }
            }}
            aria-label={mode === 'line' ? 'Show candlesticks' : 'Show line chart'}
            className="grid h-10 w-9 place-items-center text-fg"
          >
            <Icon name={mode === 'line' ? 'candles' : 'line'} size={22} />
          </button>
          <button type="button" onClick={() => setSettings(true)} aria-label="Chart settings" className="grid h-10 w-9 place-items-center text-fg">
            <Icon name="settings" size={21} />
          </button>
        </div>
        {settings && <ChartSettings symbol={symbol} onClose={closeSettings} sheet />}
      </div>
    );
  }

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1 border-b border-line px-3 py-1.5">
        <div role="group" aria-label="Timeframe" className="flex gap-0.5">
          {TFS.map((t) => (
            <button
              key={t.id}
              type="button"
              aria-pressed={tf === t.id}
              onClick={() => pickTf(t.id)}
              className={cx('num h-6 rounded-[5px] px-2 text-[11.5px]', tf === t.id ? 'bg-raised text-fg' : 'text-muted hover:text-fg')}
            >
              {t.label}
            </button>
          ))}
        </div>
        <span className="h-4 w-px bg-line" />
        {shown && look.legend && (
          <span className="num text-[11.5px] text-muted">
            <span className="text-fg">{candleDate(shown.t, utc, tf)}</span> · O <span className={up ? 'text-up' : 'text-down'}>{fmt(shown.o.toFixed(dp), dp)}</span> H <span className={up ? 'text-up' : 'text-down'}>{fmt(shown.h.toFixed(dp), dp)}</span> L{' '}
            <span className={up ? 'text-up' : 'text-down'}>{fmt(shown.l.toFixed(dp), dp)}</span> C <span className={up ? 'text-up' : 'text-down'}>{fmt(shown.c.toFixed(dp), dp)}</span> V{' '}
            <span className="text-fg">{fmtCompact(String(shown.v))}</span>
          </span>
        )}
        {loading && <span className="text-[11px] text-muted">Loading…</span>}
        <button
          type="button"
          onClick={resetView}
          title="Default zoom, scrolled to the latest candle"
          className="ml-auto flex h-6 items-center gap-1.5 rounded-[5px] border border-line-strong px-2 text-[11px] font-medium text-muted hover:border-accent hover:text-fg"
        >
          <Icon name="reset" size={11} />
          Reset view
        </button>
      </div>
      <div
        ref={wrap}
        className="relative min-h-[240px] flex-1"
        onPointerDownCapture={onDown}
        onPointerMove={(e) => (dragRef.current ? dragMove(e) : onMove(e))}
        onPointerUp={() => {
          dragEnd();
          scheduleSettle();
        }}
        onWheel={scheduleSettle}
        onPointerCancel={dragEnd}
        onPointerLeave={() => !dragRef.current && setOverlay((o) => (o.hover ? { ...o, hover: null } : o))}
      >
        <div ref={box} className="absolute inset-0" />
        {overlays}
      </div>
      {settings && <ChartSettings symbol={symbol} onClose={closeSettings} />}
    </div>
  );
}

/** `rgb(...)`, `#rrggbb` or a CSS colour with an alpha, for area fills. */
function withAlpha(color: string, a: number): string {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(color);
  if (m) return `rgba(${parseInt(m[1]!, 16)}, ${parseInt(m[2]!, 16)}, ${parseInt(m[3]!, 16)}, ${a})`;
  return color;
}
